import {
  type DefaultParamType,
  DevTools,
  useTranslate,
  type TFnType,
  Tolgee,
  type TolgeeInstance,
} from "@tolgee/vue";
import { FormatIcu } from "@tolgee/format-icu";
import { BackendFetch } from "@tolgee/web";
import { ref, type App } from "vue";
import { defaultLanguage } from "@/language";

const APP_NAMESPACE = "app";
const BDE_NAMESPACE = "bde";
const IOT_NAMESPACE = "iot";
const CONTENT_DELIVERY_URL = import.meta.env.VITE_APP_TOLGEE_CONTENT_DELIVERY_URL ?? "/tolgee-i18n";
const inContextEnabled =
  import.meta.env.DEV && import.meta.env.VITE_APP_TOLGEE_IN_CONTEXT_ENABLED === "true";

const appStaticData = {
  "de:app": () => import("../translations/app/de.json").then(({ default: data }) => data),
  "de-CH:app": () => import("../translations/app/de-CH.json").then(({ default: data }) => data),
  "en:app": () => import("../translations/app/en.json").then(({ default: data }) => data),
  "en-GB:app": () => import("../translations/app/en-GB.json").then(({ default: data }) => data),
  "es:app": () => import("../translations/app/es.json").then(({ default: data }) => data),
  "fr:app": () => import("../translations/app/fr.json").then(({ default: data }) => data),
  "hu:app": () => import("../translations/app/hu.json").then(({ default: data }) => data),
  "it:app": () => import("../translations/app/it.json").then(({ default: data }) => data),
  "pl:app": () => import("../translations/app/pl.json").then(({ default: data }) => data),
  "pt:app": () => import("../translations/app/pt.json").then(({ default: data }) => data),
  "uk-UA:app": () => import("../translations/app/uk-UA.json").then(({ default: data }) => data),
};

const bdeStaticData = {
  "de:bde": () => import("../translations/bde/de.json").then(({ default: data }) => data),
  "de-CH:bde": () => import("../translations/bde/de-CH.json").then(({ default: data }) => data),
  "en:bde": () => import("../translations/bde/en.json").then(({ default: data }) => data),
  "en-GB:bde": () => import("../translations/bde/en-GB.json").then(({ default: data }) => data),
  "es:bde": () => import("../translations/bde/es.json").then(({ default: data }) => data),
  "fr:bde": () => import("../translations/bde/fr.json").then(({ default: data }) => data),
  "hu:bde": () => import("../translations/bde/hu.json").then(({ default: data }) => data),
  "it:bde": () => import("../translations/bde/it.json").then(({ default: data }) => data),
  "pl:bde": () => import("../translations/bde/pl.json").then(({ default: data }) => data),
  "pt:bde": () => import("../translations/bde/pt.json").then(({ default: data }) => data),
  "uk-UA:bde": () => import("../translations/bde/uk-UA.json").then(({ default: data }) => data),
};

const iotStaticData = {
  "de:iot": () => import("../translations/iot/de.json").then(({ default: data }) => data),
  "de-CH:iot": () => import("../translations/iot/de-CH.json").then(({ default: data }) => data),
  "en:iot": () => import("../translations/iot/en.json").then(({ default: data }) => data),
  "en-GB:iot": () => import("../translations/iot/en-GB.json").then(({ default: data }) => data),
  "es:iot": () => import("../translations/iot/es.json").then(({ default: data }) => data),
  "fr:iot": () => import("../translations/iot/fr.json").then(({ default: data }) => data),
  "hu:iot": () => import("../translations/iot/hu.json").then(({ default: data }) => data),
  "it:iot": () => import("../translations/iot/it.json").then(({ default: data }) => data),
  "pl:iot": () => import("../translations/iot/pl.json").then(({ default: data }) => data),
  "pt:iot": () => import("../translations/iot/pt.json").then(({ default: data }) => data),
  "uk-UA:iot": () => import("../translations/iot/uk-UA.json").then(({ default: data }) => data),
};

let tolgee: TolgeeInstance | undefined;
let applicationLanguageChangeDepth = 0;
let languageChangeQueue: Promise<void> = Promise.resolve();
export const tolgeeRevision = ref(0);

export async function createTolgeeRuntime(initialLanguage?: string) {
  if (
    inContextEnabled &&
    (!import.meta.env.VITE_APP_TOLGEE_API_URL || !import.meta.env.VITE_APP_TOLGEE_API_KEY)
  ) {
    throw new Error(
      "Tolgee in-context editing requires VITE_APP_TOLGEE_API_URL and VITE_APP_TOLGEE_API_KEY",
    );
  }
  const builder = Tolgee().use(FormatIcu());
  if (inContextEnabled) {
    builder.use(DevTools());
  }
  builder.use(
    BackendFetch({
      prefix: CONTENT_DELIVERY_URL,
      fallbackOnFail: true,
      timeout: 5_000,
    }),
  );

  const normalizedLanguage = initialLanguage?.replaceAll("_", "-") ?? defaultLanguage;
  tolgee = builder.init({
    language: normalizedLanguage,
    defaultLanguage,
    defaultNs: APP_NAMESPACE,
    availableNs: [APP_NAMESPACE, BDE_NAMESPACE, IOT_NAMESPACE],
    staticData: { ...appStaticData, ...bdeStaticData, ...iotStaticData },
    // Static catalogs are complete. Only regional variants intentionally fall back.
    fallbackLanguage: {
      "de-CH": "de",
      "en-GB": "en",
    },
    ...(inContextEnabled
      ? {
          apiUrl: import.meta.env.VITE_APP_TOLGEE_API_URL,
          apiKey: import.meta.env.VITE_APP_TOLGEE_API_KEY,
          projectId: import.meta.env.VITE_APP_TOLGEE_PROJECT_ID,
        }
      : {}),
  });
  tolgee.on("update", () => {
    tolgeeRevision.value += 1;
  });
  await tolgee.run();
  return tolgee;
}

/** Provides Tolgee's composition context without installing its global `$t` or `$tolgee` helpers. */
export function provideTolgeeContext(app: App, runtime: TolgeeInstance) {
  app.provide("tolgeeContext", ref({ tolgee: runtime, isInitialRender: false }));
}

export function changeTolgeeLanguage(language: string): Promise<void> {
  const normalizedLanguage = language.replaceAll("_", "-");
  const change = async () => {
    if (!tolgee || tolgee.getLanguage() === normalizedLanguage) return;
    applicationLanguageChangeDepth += 1;
    try {
      await tolgee.changeLanguage(normalizedLanguage);
    } finally {
      applicationLanguageChangeDepth -= 1;
    }
  };
  languageChangeQueue = languageChangeQueue.then(change, change);
  return languageChangeQueue;
}

type TranslationArgument = unknown;

function addTranslationArgument(
  argument: TranslationArgument,
  position: number,
  params: Record<string, DefaultParamType>,
  options: { defaultValue?: string; language?: string },
) {
  if (argument === undefined) return;
  if (typeof argument === "number") {
    params.count = argument;
    return;
  }
  if (typeof argument === "string") {
    if (position === 0) options.defaultValue = argument;
    else options.language = argument.replaceAll("_", "-");
    return;
  }
  if (Array.isArray(argument)) {
    Object.assign(params, Object.fromEntries(argument.map((value, index) => [index, value])));
    return;
  }

  if (argument === null || typeof argument !== "object") return;

  const {
    locale,
    plural,
    named,
    list,
    default: defaultValue,
    ...namedParams
  } = argument as Record<string, unknown>;
  if (typeof locale === "string") options.language = locale.replaceAll("_", "-");
  if (typeof plural === "number") params.count = plural;
  if (typeof defaultValue === "string") options.defaultValue = defaultValue;
  if (named && typeof named === "object" && !Array.isArray(named)) Object.assign(params, named);
  if (Array.isArray(list)) {
    Object.assign(params, Object.fromEntries(list.map((value, index) => [index, value])));
  }
  Object.assign(params, namedParams);
}

/** Formats an application message through Tolgee, including ICU plurals and interpolation. */
export function translateApp(key: string, ...arguments_: TranslationArgument[]): string {
  return translateNamespace(APP_NAMESPACE, key, arguments_);
}

function translateNamespace(
  namespace: string,
  key: string,
  arguments_: TranslationArgument[],
): string {
  if (!tolgee) throw new Error("Tolgee must be initialized before translating application keys");
  const params: Record<string, DefaultParamType> = {};
  const options: { defaultValue?: string; language?: string } = {};
  arguments_.forEach((argument, position) =>
    addTranslationArgument(argument, position, params, options),
  );
  return tolgee.t({
    key,
    ns: namespace,
    ...(Object.keys(params).length > 0 ? { params } : {}),
    ...options,
  });
}

export function hasAppTranslation(key: string, language?: string): boolean {
  return hasNamespaceTranslation(APP_NAMESPACE, key, language);
}

function hasNamespaceTranslation(namespace: string, key: string, language?: string): boolean {
  if (!tolgee) return false;
  const activeTolgee = tolgee;
  const requestedLanguage = (language ?? activeTolgee.getLanguage() ?? defaultLanguage).replaceAll(
    "_",
    "-",
  );
  const languages = [
    requestedLanguage,
    ...(requestedLanguage === "de-CH" ? ["de"] : requestedLanguage === "en-GB" ? ["en"] : []),
  ];
  return languages.some((candidate) => {
    const record = activeTolgee.getRecord({ language: candidate, namespace });
    return record?.data[key] != null;
  });
}

export function getAppTranslationsByPrefix(
  prefix: string,
  language?: string,
): Record<string, string> {
  if (!tolgee) return {};
  const activeTolgee = tolgee;
  const requestedLanguage = (language ?? activeTolgee.getLanguage() ?? defaultLanguage).replaceAll(
    "_",
    "-",
  );
  const record = activeTolgee.getRecord({
    language: requestedLanguage,
    namespace: APP_NAMESPACE,
  });
  return Object.fromEntries(
    Object.entries(record?.data ?? {}).filter(
      (entry): entry is [string, string] => entry[0].startsWith(prefix) && entry[1] != null,
    ),
  );
}

export function synchronizeTolgeeLanguage(
  changeApplicationLanguage: (language: string) => Promise<unknown>,
) {
  if (!tolgee) throw new Error("Tolgee must be initialized before synchronizing its language");
  return tolgee.on("language", ({ value: language }) => {
    if (applicationLanguageChangeDepth > 0) return;
    void changeApplicationLanguage(language);
  });
}

function useNamespaceTranslate(namespace: string) {
  const { t: translationRef, isLoading } = useTranslate(namespace);
  const t = ((...args: unknown[]) =>
    (translationRef.value as (...parameters: unknown[]) => string)(...args)) as TFnType;
  return { t, isLoading };
}

export function useAppTranslate() {
  return useNamespaceTranslate(APP_NAMESPACE);
}

/**
 * Translates only static BDE catalog keys. Database-owned dynamic.* keys remain outside Tolgee and
 * are handled by the dedicated dynamic translation runtime.
 */
export function useBdeTranslate() {
  return useNamespaceTranslate(BDE_NAMESPACE);
}

/** Translates labels owned by the isolated IoT module. */
export function useIoTTranslate() {
  return useNamespaceTranslate(IOT_NAMESPACE);
}

export type AppTranslate = ReturnType<typeof useAppTranslate>["t"];
export type BdeTranslate = ReturnType<typeof useBdeTranslate>["t"];
export type IoTTranslate = ReturnType<typeof useIoTTranslate>["t"];

export { inContextEnabled as isTolgeeInContextEnabled };
